import {Injectable} from "@angular/core";
import { RawMaterialDto } from "@sharedcommon/rest/data.restservice";
import { SampleDto } from "@sharedcommon/model/sample/sample.model";
import {List} from "immutable";
import {BehaviorSubject} from "rxjs";

/**
 * Store to handle the multi modifications for raw material or sample
 */
@Injectable()
export class RmOrSampleMultiModificationStore {
  private _currentRmOrSampleId: string;
  private readonly _rmOrSamplesToProcess: BehaviorSubject<List<RmOrSampleMultiModificationDto>> = new BehaviorSubject(List([]));
  private readonly _processState: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly _rmOrSampleSaved: BehaviorSubject<string> = new BehaviorSubject("");

  start(rmOrSamples: (RawMaterialDto|SampleDto)[]): void {
    this._processState.next(true);
    const rmModifs = rmOrSamples
                      .sort((rmOrSample1, rmOrSample2) => rmOrSample1.code.localeCompare(rmOrSample2.code))
                      .map(rm => new RmOrSampleMultiModificationDto(rm));
    this._rmOrSamplesToProcess.next(List(rmModifs));
  }

  stop(): void {
    this._processState.next(false);
    this._rmOrSamplesToProcess.next(List([]));
  }

  /**
   * Check if there is a multi modification ongoing
   *
   * @returns true if there is a multi modification ongoing
   */
  isProcessActive(): boolean {
    return this._processState.getValue();
  }

  /**
   * Indicates that the current rm or sample has been saved
   */
  setCurrentRmOrSampleAsSaved() {
    this._rmOrSampleSaved.next(this._currentRmOrSampleId);
  }

  get rmOrSamplesToProcess(): BehaviorSubject<Immutable.List<RmOrSampleMultiModificationDto>> {
    return this._rmOrSamplesToProcess;
  }

  get currentRmOrSampleId(): string {
    return this._currentRmOrSampleId;
  }

  set currentRmOrSampleId(value: string) {
    this._currentRmOrSampleId = value;
  }

  get processState(): BehaviorSubject<boolean> {
    return this._processState;
  }

  get rmOrSampleSaved(): BehaviorSubject<string> {
    return this._rmOrSampleSaved;
  }
}

export class RmOrSampleMultiModificationDto {

  constructor(rmOrSample: RawMaterialDto|SampleDto) {
    this.id = rmOrSample.id;
    this.code = rmOrSample.code;
    this.label = `${rmOrSample.code} - ${rmOrSample.label}`;
    this.state = RmOrSampleMultiModificationStatus.NOT_REACHED;
  }

  id: string;
  code: string;
  label: string;
  state: RmOrSampleMultiModificationStatus;
}

export enum RmOrSampleMultiModificationStatus {
  NOT_REACHED = "Not reached",
  NOT_MODIFIED = "Not modified",
  MODIFIED = "Modified"
}
